import type { api } from '@udm/contracts';
import { Building2, ChevronDown, ChevronRight, ListChecks, Pencil, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isClientUser } from '../../api/client';
import { Card, PageHeader } from '../../ui/components';
import { formatNumber } from '../../ui/format';
import { ErrorNote } from '../admin/ErrorNote';
import { useMe } from '../auth/session';
import { decidesPlacement, PlacementReview } from './PlacementReview';
import { SITE_ICON, SiteEditor } from './SiteEditor';
import { useAdminSites } from './sitesData';

type Site = api.Site;

type DrawerState = { mode: 'edit'; id: string; created?: true } | { mode: 'create'; parent: Site | null };

export function SitesPage() {
  const { t } = useTranslation();
  const sites = useAdminSites();
  const location = useLocation();
  const reopen = (location.state as { open?: string } | null)?.open;
  const [drawer, setDrawer] = useState<DrawerState | null>(reopen ? { mode: 'edit', id: reopen } : null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const items = useMemo(() => sites.data?.items ?? [], [sites.data]);
  const ids = useMemo(() => new Set(items.map((s) => s.id)), [items]);
  const children = useMemo(() => {
    const m = new Map<string | null, Site[]>();
    // A site whose parent is not visible to the caller is shown as a root of its own area.
    for (const s of items) {
      const key = s.parent_id && ids.has(s.parent_id) ? s.parent_id : null;
      m.set(key, [...(m.get(key) ?? []), s]);
    }
    for (const list of m.values()) list.sort((a, b) => a.name.localeCompare(b.name));
    return m;
  }, [items, ids]);
  const totals = useMemo(() => {
    const total = new Map<string, number>();
    const sum = (s: Site): number => {
      const n = s.device_count + (children.get(s.id) ?? []).reduce((acc, c) => acc + sum(c), 0);
      total.set(s.id, n);
      return n;
    };
    (children.get(null) ?? []).forEach(sum);
    return total;
  }, [children]);
  const selected = drawer?.mode === 'edit' ? items.find((s) => s.id === drawer.id) : undefined;
  const clients = items.filter((s) => s.kind === 'organization').length;
  // U13: a client account works inside its own client — show its localities and zones, not the client row.
  const me = useMe();
  const clientUser = isClientUser(me.data);
  const superadmin = decidesPlacement(me.data);
  // Back from "view on map" of the placement review: the review opens again.
  const [reviewAll, setReviewAll] = useState(
    (location.state as { placementReview?: string } | null)?.placementReview === 'all',
  );
  const reviewAllBtn = useRef<HTMLButtonElement>(null);
  const ownClient = clientUser ? items.find((s) => s.kind === 'organization') : undefined;

  const toggle = (id: string) =>
    setCollapsed((c) => {
      const n = new Set(c);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const renderRows = (parent: string | null, depth: number): React.ReactNode =>
    (children.get(parent) ?? []).map((s) => {
      const Icon = SITE_ICON[s.kind];
      const kids = children.get(s.id) ?? [];
      const open = !collapsed.has(s.id);
      return (
        <div key={s.id}>
          <div
            className={`tree-row${selected?.id === s.id ? ' row-selected' : ''}`}
            style={{ paddingLeft: 8 + depth * 22 }}
            onClick={() => setDrawer({ mode: 'edit', id: s.id })}
          >
            <button
              type="button"
              className="tree-toggle"
              style={{ visibility: kids.length ? 'visible' : 'hidden' }}
              onClick={(e) => (e.stopPropagation(), toggle(s.id))}
              aria-label={open ? t('nav.collapse') : t('nav.expand')}
            >
              {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
            <Icon size={16} className="muted" />
            <span className="ellipsis" style={{ fontWeight: s.kind === 'organization' ? 600 : 400 }}>
              {s.name}
            </span>
            <span className="kind-badge">{t(`sites.kind.${s.kind}`)}</span>
            {s.status === 'archived' && <span className="badge badge-unknown">{t('sites.archived')}</span>}
            <span style={{ flex: 1 }} />
            <span className="muted num hide-mobile">
              {t('sites.devices', { formatted: formatNumber(totals.get(s.id) ?? 0) })}
            </span>
            <span className="tree-actions">
              {s.can_add_child && (
                <button
                  type="button"
                  className="icon-btn"
                  title={t('sites.addChild')}
                  aria-label={t('sites.addChild')}
                  onClick={(e) => (e.stopPropagation(), setDrawer({ mode: 'create', parent: s }))}
                >
                  <Plus size={16} />
                </button>
              )}
              {s.can_manage && (
                <button
                  type="button"
                  className="icon-btn"
                  title={t('common.edit')}
                  aria-label={t('common.edit')}
                  onClick={(e) => (e.stopPropagation(), setDrawer({ mode: 'edit', id: s.id }))}
                >
                  <Pencil size={15} />
                </button>
              )}
            </span>
          </div>
          {open && renderRows(s.id, depth + 1)}
        </div>
      );
    });

  return (
    <div className="page">
      <PageHeader
        title={ownClient ? t('sites.titleClient') : t('sites.title')}
        subtitle={
          ownClient
            ? t('sites.summaryClient', { client: ownClient.name, sites: items.length - 1 })
            : t('sites.summary', { clients, sites: items.length })
        }
        actions={
          <>
            {superadmin && (
              <button ref={reviewAllBtn} type="button" className="btn" onClick={() => setReviewAll(true)}>
                <ListChecks size={16} /> {t('placement.checkAll')}
              </button>
            )}
            {sites.data?.can_create_client && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setDrawer({ mode: 'create', parent: null })}
              >
                <Plus size={16} /> {t('sites.newClient')}
              </button>
            )}
          </>
        }
      />
      {reviewAll && (
        <PlacementReview
          siteId={null}
          sites={items}
          canDecide
          onClose={() => setReviewAll(false)}
          returnFocus={reviewAllBtn}
        />
      )}
      <Card bodyClass="">
        {sites.isLoading ? (
          <div className="card-body muted">{t('common.loading')}</div>
        ) : sites.isError ? (
          <div className="card-body">
            <ErrorNote error={sites.error} />
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <Building2 size={32} className="muted" />
            <strong>{t('sites.empty')}</strong>
            <span className="muted">{t('sites.emptyHint')}</span>
          </div>
        ) : (
          renderRows(ownClient?.id ?? null, 0)
        )}
      </Card>
      {drawer && (drawer.mode === 'create' || selected) && (
        <SiteEditor
          key={drawer.mode === 'edit' ? drawer.id : `new-${drawer.parent?.id ?? 'root'}`}
          site={selected}
          parent={drawer.mode === 'create' ? drawer.parent : undefined}
          subSites={
            selected
              ? (children.get(selected.id) ?? []).map((c) => ({ site: c, devices: totals.get(c.id) ?? 0 }))
              : []
          }
          deviceTotal={selected ? (totals.get(selected.id) ?? 0) : 0}
          onClose={() => setDrawer(null)}
          created={drawer.mode === 'edit' && drawer.created}
          onCreated={(s) => setDrawer({ mode: 'edit', id: s.id, created: true })}
          onAddChild={(s) => setDrawer({ mode: 'create', parent: s })}
          onOpen={(id) => setDrawer({ mode: 'edit', id })}
        />
      )}
    </div>
  );
}
